import type { Router } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useJoinStore } from '@/stores/join'

export function setupRouterGuards(router: Router) {
  router.beforeEach(async (to, _from, next) => {
    const authStore = useAuthStore()

    // Set page title
    document.title = to.meta.title
      ? `${to.meta.title} - PEACE`
      : 'PEACE'

    // Hard-block developer-tool routes outside dev builds (defense-in-depth: the
    // dev-only routes are already tree-shaken out of production, but if one ever
    // ships by mistake, this code-layer gate keeps it unreachable). See router/index.ts.
    if (to.meta.devOnly && !import.meta.env.DEV) {
      next({ name: 'not-found' })
      return
    }

    // Check authentication
    if (to.meta.requiresAuth && !authStore.isAuthenticated) {
      next({ name: 'login', query: { redirect: to.fullPath } })
      return
    }

    // Onboarding step guard (defense-in-depth)
    if (to.meta.joinStep !== undefined) {
      const joinStore = useJoinStore()
      if (!joinStore.status) {
        try {
          await joinStore.fetchStatus()
        } catch (e) {
          // soft gate, ignore status load errors
        }
      }
      const currentIndex = joinStore.currentStepIndex
      const targetIndex = to.meta.joinStep
      if (targetIndex > currentIndex + 1) {
        const currentAllowedStep = joinStore.steps.find(s => s.current)
        if (currentAllowedStep && currentAllowedStep.route) {
          next(currentAllowedStep.route)
          return
        } else {
          next({ name: 'join-status' })
          return
        }
      }
    }

    // Check roles (use activeIdentity for non-admin routes)
    if (to.meta.roles && to.meta.roles.length > 0) {
      const requiredRoles = to.meta.roles as string[]
      const isAdminRoute = requiredRoles.some(r => ['site_admin', 'site_owner'].includes(r))

      if (isAdminRoute) {
        if (!requiredRoles.includes(authStore.user?.role ?? '')) {
          next({ name: 'forbidden' })
          return
        }
        authStore.resetIdentityToPrimary()
        next()
        return
      } else {
        const heldRequired = requiredRoles.find(r => authStore.user?.roles?.includes(r))
        if (!heldRequired) {
          next({ name: 'forbidden' })
          return
        }
        const current = authStore.currentIdentity
        const currentIsValid =
          !!current &&
          requiredRoles.includes(current) &&
          (current === authStore.user?.role || (authStore.user?.roles?.includes(current) ?? false))
        if (!currentIsValid) {
          next({ name: 'identity-switch-confirm', query: { to: heldRequired, next: to.fullPath } })
          return
        }
        next()
        return
      }
    }

    next()
  })
}
